import { replaceTempUUid } from "api/exchanges/exchangeUtils";
import type {
  ExchangeClient,
  ExchangeOperation,
  ExchangeResponse,
} from "api/exchanges/types";

type NodeData = {
  id: string;
  referencePosition?: number;
  nodes?: NodeData[];
};

type CopyNodeResponseData = {
  copyProjectNode?: {
    id: string;
    nodes?: NodeData[];
  };
};

type ActionOnGetCopyNodeResponseParams = {
  response: ExchangeResponse & {
    data?: CopyNodeResponseData;
  };
  queue: Set<ExchangeOperation>;
  replaceKeysMap: Map<string, string>;
};

export const actionOnGetCopyNodeResponse = ({
  response,
  queue,
  replaceKeysMap,
}: ActionOnGetCopyNodeResponseParams): void => {
  if (queue.size !== 0 && response?.data?.copyProjectNode) {
    const pastedItemTempId = `temp_copy_${response.operation?.variables?.input?.title?.replace(/[^\w-]/g, "") || ""}__${response?.operation?.variables?.destinationId}__${response?.operation?.variables?.referencePosition}`;
    const { id, nodes } = response?.data?.copyProjectNode || {};
    if (id) {
      replaceKeysMap.set(pastedItemTempId, id);
    }

    if (nodes?.length !== 0) {
      collectAllReplacementKeys({
        nodes,
        parentId: pastedItemTempId,
        replaceKeysMap,
      });
    }
  }
};

const collectAllReplacementKeys = ({
  nodes,
  parentId,
  replaceKeysMap,
}: {
  nodes?: NodeData[];
  parentId: string;
  replaceKeysMap: Map<string, string>;
}): void => {
  nodes?.forEach((node) => {
    const idToReplace = `temp__${parentId}__${node.referencePosition}`;
    replaceKeysMap.set(idToReplace, node.id);
    if (node?.nodes && node?.nodes.length > 0) {
      collectAllReplacementKeys({
        nodes: node.nodes,
        parentId: idToReplace,
        replaceKeysMap,
      });
    }
  });
};

type ExeCopyNodeFromQueueParams = {
  operation: ExchangeOperation;
  client: ExchangeClient;
  queue: Set<ExchangeOperation>;
  replaceKeysMap: Map<string, string>;
};

export const exeCopyNodeFromQueue = ({
  operation,
  client,
  queue,
  replaceKeysMap,
}: ExeCopyNodeFromQueueParams): void => {
  const { destinationId, id } = operation.variables ?? {};

  if (destinationId?.startsWith("temp_")) {
    if (operation.variables) {
      operation.variables.destinationId = replaceTempUUid({
        id: destinationId,
        replaceKeysMap,
      });
    }
  }
  if (id?.startsWith("temp_")) {
    if (operation.variables) {
      operation.variables.id = replaceTempUUid({
        id,
        replaceKeysMap,
      });
    }
  }

  client.reexecuteOperation({
    ...operation,
    context: {
      ...operation.context,
      requestPolicy: "network-only",
      fromQueue: true,
    },
  });
  queue.delete(operation);
};
